<template>
  <div>

    <Head :title="`Group: ${group.name}`" />

    <!-- Navigation -->
    <div class="flex items-center justify-between mb-6">
      <Link class="btn-gray" href="/accessory-groups">
      ← Back to Groups
      </Link>
      <Link class="btn-blue" :href="`/accessory-groups/${group.id}/edit`">
      Manage Accessories
      </Link>
    </div>

    <!-- Page Header -->
    <div class="mb-8">
      <div class="flex items-center gap-3">
        <h1 class="text-3xl font-bold">{{ group.name }}</h1>
        <span v-if="group.is_active" class="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
          Active
        </span>
        <span v-else class="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm font-medium">
          Inactive
        </span>
      </div>
      <p v-if="group.description" class="text-gray-600 mt-2">{{ group.description }}</p>
      <p class="text-sm text-gray-500 mt-1">
        System: {{ group.system?.name || 'All Systems' }} •
        Created: {{ group.created_at }}
      </p>
    </div>

    <!-- Accessories in this group -->
    <div class="bg-white shadow overflow-hidden sm:rounded-md">
      <div class="px-4 py-5 bg-gray-50 sm:px-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          Accessories in {{ group.name }}
        </h3>
        <p class="mt-1 max-w-2xl text-sm text-gray-500">
          {{ accessories.length }} accessories configured for this group.
        </p>
      </div>

      <div v-if="accessories.length > 0" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Part Number
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                System
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Optional
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tooltip
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Formula
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="accessory in accessories" :key="accessory.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">
                  {{ accessory.part_number }}
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900">
                  {{ accessory.description || '-' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  {{ accessory.system }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span v-if="accessory.is_optional"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Yes
                </span>
                <span v-else
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  No
                </span>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900 max-w-xs truncate" :title="accessory.tooltip">
                  {{ accessory.tooltip || '-' }}
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900 max-w-xs truncate" :title="accessory.calculation_formula">
                  {{ accessory.calculation_formula || '-' }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-12">
        <div class="text-gray-500">
          <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path
              d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0124 24c4.004 0 7.625 2.371 9.287 6.086M30 14a6 6 0 11-12 0 6 6 0 0112 0z"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">No accessories</h3>
          <p class="mt-1 text-sm text-gray-500">
            Get started by adding accessories to this group.
          </p>
          <div class="mt-6">
            <Link :href="`/accessory-groups/${group.id}/edit`" class="btn-blue">
              Add Accessories
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    group: Object,
    accessories: Array,
  },
}
</script>